/**
 * @file ComparePlansTable.tsx
 * @brief "Compare plans" under the "Your plan" card, on the personal
 *        Billing → Plan and the company admin's Billing. Web and the Mac app.
 *
 * @details
 * DES 21-10-2026, Granola style: one column per plan with its name, price
 * and button in the head, then one row per catalogue field. Every figure is
 * GET /billing/subscriptions/catalog (planCompare.ts), so a Super Admin
 * price or limit change shows here at once; nothing is hardcoded.
 * - Columns: personal = Free, Pro, Enterprise; company admin = Free,
 *   Business, Enterprise, from `compare` ([free, ...catalog]); `catalog`
 *   stays the buyable list.
 * - The current plan's column is tinted and says "Current plan".
 * - The page supplies the self-serve button (`action`): it owns the buy and
 *   change flows. Enterprise is "Contact sales", a mail to support for now.
 * - No VAT words (W36, 01-11-2026): VERBLIZR is not VAT registered, so
 *   "VAT included" was misleading. The VAT wording switch
 *   (VITE_PRICE_VAT_LABEL, off) stays for when it is.
 * The phone has no copy of this (Apple rule: no prices on the phone).
 *
 * @author DES
 * @date 21-10-2026
 */

import { useState, type ReactNode } from "react";
import { Check, Mail } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { usePlanCatalog } from "@/features/my-billing/api";
import { price } from "@/features/my-billing/components/format";
import { buildCompareRows, comparePlansFor, type ComparePlan } from "@/features/my-billing/planCompare";
import type { BillingInterval } from "@/features/my-billing/planConfirm";
import { InfoTip } from "@/components/ui/InfoTip";
import { cn } from "@/shared/theme/cn";
import { FALLBACKS, mailto } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";
import { featureRows, planTips, priceTip, ROW_TIP } from "./planTips";

/** "Contact sales" mails SALES_EMAIL (Links & company info, B23); support@ before. */
function useSalesMail(): string {
  const links = usePublicConfig();
  return mailto(links.value("SALES_EMAIL", FALLBACKS.SALES_EMAIL), "VERBLIZR Enterprise");
}

const isContract = (p: ComparePlan) => !!(p.enterprise || p.contactSales);

/** The column head: name, price for the chosen interval, button. */
function PlanHead({
  plan,
  interval,
  current,
  action,
}: {
  plan: ComparePlan;
  interval: BillingInterval;
  current: boolean;
  action: ReactNode;
}) {
  const contract = isContract(plan);
  const salesMail = useSalesMail();
  const free = plan.priceMinor === 0;
  const amount = interval === "year" ? plan.annualPriceMinor : plan.priceMinor;
  return (
    <div className="flex h-full flex-col gap-1">
      <div className="flex min-h-6 flex-wrap items-center gap-2">
        <span className="text-base font-semibold tracking-tight text-foreground">{plan.name}</span>
        {current ? (
          <span className="rounded-full bg-brand/[0.12] px-2 py-0.5 text-[11px] font-medium text-brand">Current plan</span>
        ) : interval === "year" && plan.annualSavingPct && !free ? (
          <Badge variant="success">Save {plan.annualSavingPct}%</Badge>
        ) : null}
      </div>
      <p className="flex items-center gap-1.5 text-2xl font-semibold tracking-tight text-foreground">
        {contract ? "Custom" : free ? price(0, plan.currency) : amount != null ? price(amount, plan.currency) : "—"}
        <InfoTip label={`${plan.name} price`} text={priceTip(plan)} />
      </p>
      <p className="min-h-4 text-xs text-muted-foreground">
        {contract || free
          ? ""
          : amount == null
              ? "Monthly only"
              : `${plan.perSeat ? "per seat " : ""}/ ${interval === "year" ? "year" : "month"}`}
      </p>
      <div className="mt-auto pt-3">
        {contract ? (
          <Button asChild size="sm" variant="outline" className="w-full">
            <a href={salesMail}>
              <Mail className="h-4 w-4" />
              Contact sales
            </a>
          </Button>
        ) : (
          action
        )}
      </div>
    </div>
  );
}

export function ComparePlansTable({
  audience,
  currentCode,
  action,
}: {
  audience: "personal" | "company_admin";
  /** The account's plan code ("free" with no paid plan). */
  currentCode: string;
  /** The button (or note) under a self-serve plan; null for none. */
  action: (plan: ComparePlan, interval: BillingInterval) => ReactNode;
}) {
  const catalog = usePlanCatalog();
  const [interval, setInterval] = useState<BillingInterval>("month");

  if (catalog.isLoading) return <SkeletonRows rows={3} />;
  // The plan card above still works; the table is extra, so fail quietly.
  if (catalog.isError || !catalog.data || catalog.data.eligible === false) return null;

  const plans = comparePlansFor(catalog.data, audience);
  if (plans.filter((p) => !isContract(p)).length === 0) return null;
  // Catalogue rows, then what each account can use (planTips FEATURE_ROWS).
  const rows = [...buildCompareRows(plans), ...featureRows(plans, audience)];
  // Tip numbers from the whole `compare` list, so Free's figures are in them.
  const tips = planTips(catalog.data.compare ?? catalog.data.catalog);
  const yearly = plans.some((p) => !isContract(p) && p.annualPriceMinor != null && p.priceMinor !== 0);
  const tint = (p: ComparePlan) => (p.code === currentCode ? "bg-black/[0.035] dark:bg-white/[0.045]" : "");

  return (
    <GridBand className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold tracking-tight">Compare plans</h3>
        {yearly ? (
          <div role="radiogroup" aria-label="Billing interval" className="inline-flex rounded-full bg-muted p-1 text-sm">
            {(["month", "year"] as const).map((iv) => (
              <button
                key={iv}
                type="button"
                role="radio"
                aria-checked={interval === iv}
                onClick={() => setInterval(iv)}
                className={cn(
                  "rounded-full px-4 py-1.5 font-medium transition-colors",
                  interval === iv ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {iv === "month" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="-mx-1 overflow-x-auto px-1">
        <table className="w-full min-w-[600px] table-fixed border-separate border-spacing-0 text-sm">
          <colgroup>
            <col className="w-[28%]" />
            {plans.map((p) => (
              <col key={p.code} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="align-bottom pb-3 pr-4 text-left">
                <span className="sr-only">What you get</span>
              </th>
              {plans.map((p) => (
                <th
                  key={p.code}
                  scope="col"
                  className={cn(
                    "rounded-t-2xl px-4 pb-4 pt-4 text-left align-top font-normal",
                    tint(p),
                  )}
                  aria-current={p.code === currentCode ? "true" : undefined}
                >
                  <PlanHead
                    plan={p}
                    interval={interval}
                    current={p.code === currentCode}
                    action={isContract(p) ? null : action(p, interval)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={r.label}>
                <th
                  scope="row"
                  className="border-t border-border/60 py-3 pr-4 text-left align-top font-normal text-muted-foreground"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {r.label}
                    {ROW_TIP[r.label] ? <InfoTip label={r.label} text={tips[ROW_TIP[r.label]]} /> : null}
                  </span>
                  {(r as { sub?: string }).sub ? (
                    <span className="mt-0.5 block text-xs leading-snug text-muted-foreground/80">
                      {(r as { sub?: string }).sub}
                    </span>
                  ) : null}
                </th>
                {r.cells.map((c, i) => (
                  <td
                    key={plans[i].code}
                    className={cn(
                      "border-t border-border/60 px-4 py-3 align-top text-foreground",
                      tint(plans[i]),
                      ri === rows.length - 1 && "rounded-b-2xl",
                      (c === "No" || c === "—" || c === "None included") && "text-muted-foreground",
                    )}
                  >
                    {c === "Yes" ? (
                      <>
                        <Check className="h-4 w-4 text-foreground" aria-hidden />
                        <span className="sr-only">Yes</span>
                      </>
                    ) : (
                      c
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GridBand>
  );
}
